import React, { useCallback, useMemo } from 'react';
import { Button, Space, Table, Tooltip } from 'antd';
import { BankOutlined } from '@ant-design/icons';

export type UsersTableProps = {
  users: any[];
  permissionsByUser: Record<string, any>;
  loading: boolean;
  onGrantCompany: (user: any) => void;
  onLoadMore: () => void;
  onViewCompanyAccess: (user: any) => void;
};

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  permissionsByUser,
  loading,
  onGrantCompany,
  onLoadMore,
  onViewCompanyAccess,
}) => {
  const formatDate = useCallback((value: any) => {
    if (!value) return '-';
    const d = typeof value?.toDate === 'function' ? value.toDate() : (value instanceof Date ? value : null);
    if (d) return d.toLocaleDateString();
    return String(value);
  }, []);

  const getAccessCount = useCallback((perm: any, field: 'companyAccess') => {
    const map = perm?.[field] || {};
    if (map['*']) return 'All';
    const keys = Object.keys(map).filter(k => k !== '*');
    return keys.length;
  }, []);

  const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const handleScroll = useCallback(({ target }: any) => {
    if (loading) return;
    const { scrollTop, scrollHeight, clientHeight } = target as HTMLElement;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      onLoadMore();
    }
  }, [loading, onLoadMore]);

  const debounceScroll = useMemo(() => debounce(handleScroll, 200), [handleScroll]);

  const columns = useMemo(() => ([
    { title: 'Email', dataIndex: 'email' },
    {
      title: 'Name',
      render: (_: any, r: any) => `${r?.firstName || ''} ${r?.lastName || ''}`.trim() || '-',
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      render: (v: any) => formatDate(v),
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      render: (v: any) => formatDate(v),
    },
    {
      title: 'Companies',
      render: (_: any, r: any) => (
        <Button type="link" onClick={() => onViewCompanyAccess(r)}>
          {getAccessCount(permissionsByUser[r.id], 'companyAccess')}
        </Button>
      ),
    },
    {
      title: 'Actions',
      render: (_: any, r: any) => (
        <Space>
          <Tooltip title="Grant company access">
            <Button icon={<BankOutlined />} onClick={() => onGrantCompany(r)} />
          </Tooltip>
        </Space>
      ),
    },
  ]), [formatDate, getAccessCount, onGrantCompany, onViewCompanyAccess, permissionsByUser]);

  return (
    <Table
      size="small"
      bordered
      rowKey={(r) => r.id}
      columns={columns as any}
      dataSource={users}
      pagination={false}
      loading={loading && users.length === 0}
      scroll={{ y: 570 }}
      onScroll={debounceScroll}
    />
  );
};

export default UsersTable;
